// ts-rest contract for the Aveni Detect integration endpoints.
//
// The shape mirrors the Aveni public API (OAuth2 client_credentials at
// /v1/oauth/token, metadata submission at /v1/audio/metadata) for the
// outbound ingestion path, and locally-defined endpoints for retrieving
// stored transcripts and receiving asynchronous transcript-ready webhooks.
//
// Endpoints:
//   GET  /api/integrations/aveni/status
//   POST /api/integrations/aveni/transcripts/ingest         (legacy / stub)
//   POST /api/integrations/aveni/metadata                   (real upstream submit)
//   GET  /api/integrations/aveni/transcripts/:callId
//   POST /api/integrations/aveni/webhook

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

// ---------------------------------------------------------------------------
// Core data shapes
// ---------------------------------------------------------------------------

export const aveniRiskTagKindSchema = z.enum([
    'vulnerability',
    'complaint',
    'financial_crime',
    'suitability',
    'conduct',
    'general',
]);
export type AveniRiskTagKind = z.infer<typeof aveniRiskTagKindSchema>;

export const aveniSentimentSchema = z.enum(['positive', 'neutral', 'negative', 'escalation']);
export type AveniSentiment = z.infer<typeof aveniSentimentSchema>;

/** A single speaker turn in an Aveni transcript. */
export const aveniTranscriptTurnSchema = z.object({
    speakerId: z.string(),
    speakerLabel: z.string(), // e.g. "IM", "Client"
    offsetMs: z.number(), // milliseconds from call start
    text: z.string(),
    confidence: z.number(), // 0–1 Aveni ASR confidence
});
export type AveniTranscriptTurn = z.infer<typeof aveniTranscriptTurnSchema>;

/** A risk tag produced by Aveni's compliance-screening engine. */
export const aveniRiskTagSchema = z.object({
    id: z.string(),
    callId: z.string(),
    kind: aveniRiskTagKindSchema,
    label: z.string(), // e.g. "Vulnerable customer indicator"
    severity: z.enum(['low', 'medium', 'high', 'critical']),
    offsetMs: z.number(),
    excerpt: z.string(), // verbatim snippet that triggered the tag
});
export type AveniRiskTag = z.infer<typeof aveniRiskTagSchema>;

/** Full transcript record as stored in Septima. */
export const aveniTranscriptSchema = z.object({
    callId: z.string(),
    clientId: z.string(),
    agentId: z.string(),
    durationMs: z.number(),
    // Sentiment is authored by Aveni — do not recompute client-side.
    sentiment: aveniSentimentSchema,
    aveniConfidence: z.number(), // 0–1 overall transcript confidence
    turns: z.array(aveniTranscriptTurnSchema),
    riskTags: z.array(aveniRiskTagSchema),
    processedAt: z.string(), // ISO timestamp
    audioRef: z.string().optional(),
});
export type AveniTranscript = z.infer<typeof aveniTranscriptSchema>;

// ---------------------------------------------------------------------------
// Aveni public API — audio metadata submission
// ---------------------------------------------------------------------------
// Mirrors the AudioMetadataDto from the Aveni public OpenAPI spec
// (https://api.aveni.ai – POST /v1/audio/metadata). Only the participant
// shape is loosened slightly (recordingId/email/phoneNumber are optional
// per the upstream schema).

export const aveniParticipantTypeSchema = z.enum(['internal', 'external']);
export type AveniParticipantType = z.infer<typeof aveniParticipantTypeSchema>;

export const aveniCallDirectionSchema = z.enum([
    'incoming',
    'outgoing',
    'unknown',
    'other',
    'internal',
]);
export type AveniCallDirection = z.infer<typeof aveniCallDirectionSchema>;

export const aveniParticipantSchema = z.object({
    name: z.string().min(2),
    id: z.string().min(1),
    type: aveniParticipantTypeSchema,
    recordingId: z.string().optional(),
    email: z.string().email().optional(),
    phoneNumber: z.string().optional(),
});
export type AveniParticipant = z.infer<typeof aveniParticipantSchema>;

export const aveniOrganisationSchema = z.object({
    department: z.string().optional(),
    team: z.string().optional(),
    brand: z.string().optional(),
});
export type AveniOrganisation = z.infer<typeof aveniOrganisationSchema>;

export const aveniAudioMetadataSchema = z.object({
    fileName: z.string().min(1),
    callDateTime: z.string(),
    participants: z.array(aveniParticipantSchema).min(1),
    callType: z.string().min(1),
    caseId: z.string().optional(),
    caseType: z.string().optional(),
    callDirection: aveniCallDirectionSchema.optional(),
    callEndedAt: z.string().optional(),
    duration: z.number().int().min(0).optional(),
    organisation: aveniOrganisationSchema.optional(),
    contactId: z.string().optional(),
    externalCallId: z.string().optional(),
    originalRecordingFilename: z.string().optional(),
    audioFileUri: z.string().optional(),
});
export type AveniAudioMetadata = z.infer<typeof aveniAudioMetadataSchema>;

export const aveniSubmissionResultSchema = z.object({
    success: z.boolean(),
    id: z.string(),
    message: z.string().optional(),
});
export type AveniSubmissionResult = z.infer<typeof aveniSubmissionResultSchema>;

// ---------------------------------------------------------------------------
// Request / response schemas
// ---------------------------------------------------------------------------

export const aveniStatusResponseSchema = z.object({
    configured: z.boolean(),
    baseUrl: z.string(),
});
export type AveniStatusResponse = z.infer<typeof aveniStatusResponseSchema>;

export const aveniIngestRequestSchema = z.object({
    callId: z.string(),
    audioRef: z.string().optional(),
});
export type AveniIngestRequest = z.infer<typeof aveniIngestRequestSchema>;

export const aveniIngestResponseSchema = z.object({
    ok: z.boolean(),
    transcript: aveniTranscriptSchema,
    /**
     * True when no processed transcript is available yet — only a placeholder
     * with empty turns/risk tags was returned. Live integrations submit
     * metadata to Aveni and wait for the asynchronous `transcript.ready`
     * webhook to deliver the real transcript.
     */
    pending: z.boolean().optional(),
});
export type AveniIngestResponse = z.infer<typeof aveniIngestResponseSchema>;

export const aveniSubmitMetadataResponseSchema = z.object({
    ok: z.boolean(),
    submission: aveniSubmissionResultSchema,
});
export type AveniSubmitMetadataResponse = z.infer<typeof aveniSubmitMetadataResponseSchema>;

export const aveniWebhookPayloadSchema = z.object({
    event: z.literal('transcript.ready'),
    callId: z.string(),
    audioRef: z.string().optional(),
    data: aveniTranscriptSchema.optional(),
});
export type AveniWebhookPayload = z.infer<typeof aveniWebhookPayloadSchema>;

export const aveniWebhookResponseSchema = z.object({
    ok: z.boolean(),
});

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const aveniContract = c.router(
    {
        status: {
            method: 'GET',
            path: '/integrations/aveni/status',
            responses: {
                200: aveniStatusResponseSchema,
            },
            summary: 'Return Aveni connection status',
        },
        ingestTranscript: {
            method: 'POST',
            path: '/integrations/aveni/transcripts/ingest',
            body: aveniIngestRequestSchema,
            responses: {
                200: aveniIngestResponseSchema,
                502: z.object({ error: z.string(), code: z.literal('upstream_error') }),
            },
            summary: 'Return stored transcript (or stub) for a callId',
        },
        submitMetadata: {
            method: 'POST',
            path: '/integrations/aveni/metadata',
            body: aveniAudioMetadataSchema,
            responses: {
                200: aveniSubmitMetadataResponseSchema,
                502: z.object({ error: z.string(), code: z.literal('upstream_error') }),
            },
            summary: 'Submit call audio metadata to Aveni Detect for processing',
        },
        getTranscript: {
            method: 'GET',
            path: '/integrations/aveni/transcripts/:callId',
            pathParams: z.object({ callId: z.string() }),
            responses: {
                200: aveniTranscriptSchema,
                404: z.object({ error: z.string() }),
            },
            summary: 'Return a stored Aveni transcript by callId',
        },
        webhook: {
            method: 'POST',
            path: '/integrations/aveni/webhook',
            body: aveniWebhookPayloadSchema,
            responses: {
                200: aveniWebhookResponseSchema,
            },
            summary: 'Receive Aveni webhook events (transcript.ready)',
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);
